/*
A CVSS v4 and v41 score calculator.
Based on:
- https://www.first.org/cvss/v4.0/user-guide
- https://www.first.org/cvss/calculator/4
- https://nvd.nist.gov/vuln-metrics/cvss/v4-calculator
Emits:
 @update
Pass in individual metric (one of):
AV, AC, PR, UI, S, C, I, A, E, RL, RC, CR, IR, AR, MAV, MAC, MPR, MUI, MS, MC, MI, MA
or pass in the whole CVSS vector.
*/
import VueApexCharts from 'vue-apexcharts';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import {
    calculateCVSSv4ScoreFromMetrics,
    calculateCVSSv4MetricsFromVector
} from '@/../assets/libs/3rd-party/cvss4v2';

const RADIAL_V4_MAPPING = {
    "Attack Surface Exposure": {
        AV: { // Primary Metric Key
            N: 5, // Primary Metric Value
            A: 4,
            L: 3,
            P: 2
        }
    },
    "Ease of Exploit (Technical)": {
        AC: { // Primary Metric Key
            L: { // Primary Metric Value
                AT: { // Secondary Metric Key
                    N: { // Secondary Metric Value
                        E: { // Tertiary Metric Key
                            X: 5, // Tertiary Metric Value
                            A: 5,
                            P: 4,
                            U: 3
                        }
                    },
                    P: {
                        E: { // Tertiary Metric Key
                            X: 4, // Tertiary Metric Value
                            A: 4,
                            P: 3,
                            U: 2
                        }
                    }
                }
            },
            H: { // Primary Metric Value
                AT: { // Secondary Metric Key
                    N: { // Secondary Metric Value
                        E: { // Tertiary Metric Key
                            X: 3, // Tertiary Metric Value
                            A: 3,
                            P: 2,
                            U: 1
                        }
                    },
                    P: {
                        E: { // Tertiary Metric Key
                            X: 2, // Tertiary Metric Value
                            A: 2,
                            P: 1,
                            U: 0
                        }
                    }
                }
            }
        }
    },
    "Ease of Exploit (Privileges)": {
        PR: {
            N: 5,
            L: 3,
            H: 2
        }
    },
    "Ease of Exploit (User Interaction)": {
        UI: {
            N: 5,
            P: 3,
            A: 1
        }
    },
    "Risk to Data Confidentiality": {
        VC: {
            N: { // Primary Metric Value
                SC: { // Secondary Metric Key
                    N: 0,
                    L: 2, // Secondary Metric Value
                    H: 3,
                }
            },
            L: { // Primary Metric Value
                SC: { // Secondary Metric Key
                    N: 2,
                    L: 3, // Secondary Metric Value
                    H: 4,
                }
            },
            H: { // Primary Metric Value
                SC: { // Secondary Metric Key
                    N: 3,
                    L: 4, // Secondary Metric Value
                    H: 5,
                }
            }
        }
    },
    "Risk to Data Integrity": {
        VI: {
            N: { // Primary Metric Value
                SI: { // Secondary Metric Key
                    N: 0,
                    L: 2, // Secondary Metric Value
                    H: 3,
                }
            },
            L: { // Primary Metric Value
                SI: { // Secondary Metric Key
                    N: 2,
                    L: 3, // Secondary Metric Value
                    H: 4,
                }
            },
            H: { // Primary Metric Value
                SI: { // Secondary Metric Key
                    N: 3,
                    L: 4, // Secondary Metric Value
                    H: 5,
                }
            }
        }
    },
    "Risk to System Availability": {
        VA: {
            N: { // Primary Metric Value
                SA: { // Secondary Metric Key
                    N: 0,
                    L: 2, // Secondary Metric Value
                    H: 3,
                }
            },
            L: { // Primary Metric Value
                SA: { // Secondary Metric Key
                    N: 2,
                    L: 3, // Secondary Metric Value
                    H: 4,
                }
            },
            H: { // Primary Metric Value
                SA: { // Secondary Metric Key
                    N: 3,
                    L: 4, // Secondary Metric Value
                    H: 5,
                }
            }
        }
    }    
}


const RADAR_V4_LABEL_NAMES = {
    5: 'Very High',
    4: 'High',
    3: 'Medium',
    2: 'Low',
    1: 'Very Low',
    0: 'None',
};

export const CVSSV4Calculator = {
    props: {
        cvssVector: {
            type: String,
            default: null,
        },
        cvssAV: {
            type: String,
            default: null,
        },
        cvssAC: {
            type: String,
            default: null,
        },
        cvssAT: {
            type: String,
            default: null,
        },
        cvssPR: {
            type: String,
            default: null,
        },
        cvssUI: {
            type: String,
            default: null,
        },
        cvssVC: {
            type: String,
            default: 'N',
        },
        cvssVI: {
            type: String,
            default: 'N',
        },
        cvssVA: {
            type: String,
            default: 'N',
        },
        cvssSC: {
            type: String,
            default: 'N',
        },
        cvssSI: {
            type: String,
            default: 'N',
        },
        cvssSA: {
            type: String,
            default: 'N',
        },
        cvssS: {
            type: String,
            default: 'X',
        },
        cvssAU: {
            type: String,
            default: 'X',
        },
        cvssR: {
            type: String,
            default: 'X',
        },
        cvssV: {
            type: String,
            default: 'X',
        },
        cvssRE: {
            type: String,
            default: 'X',
        },
        cvssU: {
            type: String,
            default: 'X',
        },
        cvssMAV: {
            type: String,
            default: 'X',
        },
        cvssMAC: {
            type: String,
            default: 'X',
        },
        cvssMAT: {
            type: String,
            default: 'X',
        },
        cvssMPR: {
            type: String,
            default: 'X',
        },
        cvssMUI: {
            type: String,
            default: 'X',
        },
        cvssMVC: {
            type: String,
            default: 'X',
        },
        cvssMVI: {
            type: String,
            default: 'X',
        },
        cvssMVA: {
            type: String,
            default: 'X',
        },
        cvssMSC: {
            type: String,
            default: 'X',
        },
        cvssMSI: {
            type: String,
            default: 'X',
        },
        cvssMSA: {
            type: String,
            default: 'X',
        },
        cvssCR: {
            type: String,
            default: 'X',
        },
        cvssIR: {
            type: String,
            default: 'X',
        },
        cvssAR: {
            type: String,
            default: 'X',
        },
        cvssE: {
            type: String,
            default: 'X',
        },

        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        apexchart: VueApexCharts,
    },
    data() {
        const chartBarSeries = [
            {
                name: 'CVSS V4',
                data: [0, 0, 0],
            },
        ];
        const chartBarHeight = 125;
        var chartBarOptions = {
            chart: {
                id: 'cvss-v4-score-chart',
                height: chartBarHeight,
                type: 'bar',
            },
            stroke: {
                width: 2,
            },
            fill: {
                opacity: 0.8,
                colors: ['#428bca'],
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    horizontal: true,
                },
            },
            dataLabels: {
                enabled: false,
            },
            yaxis: {
                min: 0,
                max: 10,
                tickAmount: 10,
            },
            xaxis: {
                categories: ['CVSS-B Score'],
            },
            series: [
                {
                    name: 'Scores',
                    data: [0, 0, 0],
                },
            ],
        };
        const chartType = 'radar';
        const chartHeight = 250;
        const chartSeries = [
            {
                name: 'CVSS V4',
                data: Object.keys(RADIAL_V4_MAPPING).map(() => 0),
            },
        ];
        const chartOptions = {
            chart: {
                id: 'cvss-v4-score-chart',
                height: chartHeight,
                type: chartType,
            },
            stroke: {
                width: 2,
            },
            theme: {
                palette: 'palette2',
            },
            plotOptions: {
                radar: {
                    size: 100,
                    polygons: {
                        strokeColors: '#e9e9e9',
                        fill: {
                            colors: ['#f8f8f8', '#fff'],
                        },
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                y: {
                    formatter: (e) => RADAR_V4_LABEL_NAMES[e],
                },
            },
            colors: ['#FF4560'],
            markers: {
                size: 4,
                colors: ['#fff'],
                strokeColor: '#FF4560',
                strokeWidth: 2,
            },
            yaxis: {
                min: 0,
                max: 5,
                tickAmount: 5,
                labels: {
                    formatter: () => '', // RADAR_V4_LABEL_NAMES[e],
                },
            },
            xaxis: {
                categories: Object.keys(RADIAL_V4_MAPPING),
            },
            series: chartSeries,
        };
        return {
            badgeClass: 'badge badge-cvss',
            baseScore: null,
            qualScore: null,
            scoreType: null,
            vector: this.cvssVector ?? '-',
            AV: this.cvssAV,
            AC: this.cvssAC,
            AT: this.cvssAT,
            PR: this.cvssPR,
            UI: this.cvssUI,
            VC: this.cvssVC,
            VI: this.cvssVI,
            VA: this.cvssVA,
            SC: this.cvssSC,
            SI: this.cvssSI,
            SA: this.cvssSA,
            S: this.cvssS,
            AU: this.cvssAU,
            R: this.cvssR,
            V: this.cvssV,
            RE: this.cvssRE,
            U: this.cvssU,
            MAV: this.cvssMAV,
            MAC: this.cvssMAC,
            MAT: this.cvssMAT,
            MPR: this.cvssMPR,
            MUI: this.cvssMUI,
            MVC: this.cvssMVC,
            MVI: this.cvssMVI,
            MVA: this.cvssMVA,
            MSC: this.cvssMSC,
            MSI: this.cvssMSI,
            MSA: this.cvssMSA,
            CR: this.cvssCR,
            IR: this.cvssIR,
            AR: this.cvssAR,
            E: this.cvssE,
            chartOptions,
            chartSeries,
            chartType,
            chartHeight,
            chartBarSeries,
            chartBarHeight,
            chartBarOptions,
        };
    },
    computed: {},
    methods: {
        CVSSColour(basescore) {
            if (basescore === 0) {
                return 'cvss-zero';
            }
            if (basescore < 4.0) {
                return 'cvss-low';
            }
            if (basescore < 7.0) {
                return 'cvss-medium';
            }
            if (basescore < 9.0) {
                return 'cvss-high';
            }
            return 'cvss-critical';
        },
        calculateRadarChartScores() {
            var baseScores = []
            try{
                for (const [k, v] of Object.entries(RADIAL_V4_MAPPING)) {
                    var baseScore = 0;
                    for (const [primary_metric_key, primary_metric_value] of Object.entries(v)) {
                        if (primary_metric_value[this[primary_metric_key]].constructor == Object){
                            for (const [secondary_metric_key, secondary_metric_value] of Object.entries(primary_metric_value[this[primary_metric_key]])) {
                                if (secondary_metric_value[this[secondary_metric_key]].constructor == Object){
                                    for (const [tertiary_metric_key, tertiary_metric_value] of Object.entries(secondary_metric_value[this[secondary_metric_key]])) {
                                        baseScore = tertiary_metric_value[this[tertiary_metric_key]]
                                    }
                                } else {
                                    baseScore = secondary_metric_value[this[secondary_metric_key]]
                                }
                            }
                        } else {
                            baseScore = primary_metric_value[this[primary_metric_key]]
                        }
                        baseScores.push(baseScore)
                    } 
                }
                return baseScores;
            } catch {
            }
            return [0,0,0,0,0,0];
        },
        checkBase() {
            // returns whether all base values are selected
            return [
                this.AV,
                this.AC,
                this.AT,
                this.PR,
                this.UI,
                this.VC,
                this.VI,
                this.VA,
                this.SC,
                this.SI,
                this.SA,
            ].some((e) => !e);
        },
        updateChartBarSeries(series) {
            this.chartBarOptions.xaxis.categories = [(this.scoreType || "CVSS-B") + ' Score']
            this.chartBarSeries = series;
            this.chartBarOptions = {
                ...this.chartBarOptions,
                series,
            };
        },
        updateChartSeries(series) {
            this.chartSeries = series;
            this.chartOptions = {
                ...this.chartOptions,
                series,
            };
        },
        updateMetrics(
            AV,
            AC,
            AT,
            PR,
            UI,
            VC,
            VI,
            VA,
            SC,
            SI,
            SA,
            S,
            AU,
            R,
            V,
            RE,
            U,
            MAV,
            MAC,
            MAT,
            MPR,
            MUI,
            MVC,
            MVI,
            MVA,
            MSC,
            MSI,
            MSA,
            CR,
            IR,
            AR,
            E,
        ) {
            if (AV) this.AV = AV;
            if (AC) this.AC = AC;
            if (AT) this.AT = AT;
            if (PR) this.PR = PR;
            if (UI) this.UI = UI;
            if (VC) this.VC = VC;
            if (VI) this.VI = VI;
            if (VA) this.VA = VA;
            if (SC) this.SC = SC;
            if (SI) this.SI = SI;
            if (SA) this.SA = SA;
            if (S) this.S = S;
            if (AU) this.AU = AU;
            if (R) this.R = R;
            if (V) this.V = V;
            if (RE) this.RE = RE;
            if (U) this.U = U;
            if (MAV) this.MAV = MAV;
            if (MAC) this.MAC = MAC;
            if (MAT) this.MAT = MAT;
            if (MPR) this.MPR = MPR;
            if (MUI) this.MUI = MUI;
            if (MVC) this.MVC = MVC;
            if (MVI) this.MVI = MVI;
            if (MVA) this.MVA = MVA;
            if (MSC) this.MSC = MSC;
            if (MSI) this.MSI = MSI;
            if (MSA) this.MSA = MSA;
            if (CR) this.CR = CR;
            if (IR) this.IR = IR;
            if (AR) this.AR = AR;
            if (E) this.E = E;

        },
        updateScoreVector(
            baseScore,
            qualScore,
            scoreType,
            vectorString,
        ) {
            this.baseScore = baseScore;
            this.scoreType = scoreType;
            this.qualScore = qualScore;
            this.vector = vectorString;
            var bar_data;
            if (this.baseScore) {
                bar_data = [this.baseScore]
            } else {
                bar_data = []
            }

            this.updateChartBarSeries([
                {
                    name: 'CVSS V4',
                    data: bar_data,
                },
            ]);
            this.updateChartSeries([
                {
                    name: 'CVSS V4',
                    data: this.calculateRadarChartScores(),
                },
            ]);
            this.$emit('update', {
                baseScore: this.baseScore,
                qualScore: this.qualScore,
                scoreType: this.scoreType,
                vector: this.vector,
            });
        },
        calculateScore() {
            // try {
            const check = this.checkBase();
            if (check) {
                // some of the base selects are missing - no update possible
                return;
            }
            const cvssSelected = {
                "AV": this.AV,
                "AC": this.AC,
                "AT": this.AT,
                "PR": this.PR,
                "UI": this.UI,
                "VC": this.VC,
                "VI": this.VI,
                "VA": this.VA,
                "SC": this.SC,
                "SI": this.SI,
                "SA": this.SA,
                "S": this.S,
                "AU": this.AU,
                "R": this.R,
                "V": this.V,
                "RE": this.RE,
                "U": this.U,
                "MAV": this.MAV,
                "MAC": this.MAC,
                "MAT": this.MAT,
                "MPR": this.MPR,
                "MUI": this.MUI,
                "MVC": this.MVC,
                "MVI": this.MVI,
                "MVA": this.MVA,
                "MSC": this.MSC,
                "MSI": this.MSI,
                "MSA": this.MSA,
                "CR": this.CR,
                "IR": this.IR,
                "AR": this.AR,
                "E": this.E,
            }
            const {
                success,
                baseScore,
                qualScore,
                scoreType,
                vectorString,
            } = calculateCVSSv4ScoreFromMetrics(cvssSelected);
            if (success) {
                this.updateScoreVector(
                    baseScore,
                    qualScore,
                    scoreType,
                    vectorString,
                );
            }
            // } catch (e) {
            //     //
            // }
        },
        handleVectorChange: debounce(function handler(event) {
            try {
                const cvssSelected = {
                    "AV": this.AV,
                    "AC": this.AC,
                    "AT": this.AT,
                    "PR": this.PR,
                    "UI": this.UI,
                    "VC": this.VC,
                    "VI": this.VI,
                    "VA": this.VA,
                    "SC": this.SC,
                    "SI": this.SI,
                    "SA": this.SA,
                    "S": this.S,
                    "AU": this.AU,
                    "R": this.R,
                    "V": this.V,
                    "RE": this.RE,
                    "U": this.U,
                    "MAV": this.MAV,
                    "MAC": this.MAC,
                    "MAT": this.MAT,
                    "MPR": this.MPR,
                    "MUI": this.MUI,
                    "MVC": this.MVC,
                    "MVI": this.MVI,
                    "MVA": this.MVA,
                    "MSC": this.MSC,
                    "MSI": this.MSI,
                    "MSA": this.MSA,
                    "CR": this.CR,
                    "IR": this.IR,
                    "AR": this.AR,
                    "E": this.E,
                }
                const {
                    success,
                    baseScore,
                    qualScore,
                    scoreType,
                    vectorString,
                } = calculateCVSSv4ScoreFromMetrics(cvssSelected);

                if (success) {
                    this.updateScoreVector(
                        baseScore,
                        qualScore,
                        scoreType,
                        vectorString,
                    );
                }
            } catch (e) {
                //
            }
        }, 50),
    },
    watch: {},
    beforeDestroy() {
        if (this.$refs['cvss-v4-chart-1'] && this.$refs['cvss-v4-chart-1'].chart) {
            this.$refs['cvss-v4-chart-1'].chart.destroy();
        }
        if (this.$refs['cvss-v4-chart-2'] && this.$refs['cvss-v4-chart-2'].chart) {
            this.$refs['cvss-v4-chart-2'].chart.destroy();
        }
    },
    mounted() {
        if (this.vector && this.vector !== '-') {
            const cvssSelected = { // set to default values
                "AV": this.cvssAV,
                "AC": this.cvssAC,
                "AT": this.cvssAT,
                "PR": this.cvssPR,
                "UI": this.cvssUI,
                "VC": this.cvssVC,
                "VI": this.cvssVI,
                "VA": this.cvssVA,
                "SC": this.cvssSC,
                "SI": this.cvssSI,
                "SA": this.cvssSA,
                "S": this.cvssS,
                "AU": this.cvssAU,
                "R": this.cvssR,
                "V": this.cvssV,
                "RE": this.cvssRE,
                "U": this.cvssU,
                "MAV": this.cvssMAV,
                "MAC": this.cvssMAC,
                "MAT": this.cvssMAT,
                "MPR": this.cvssMPR,
                "MUI": this.cvssMUI,
                "MVC": this.cvssMVC,
                "MVI": this.cvssMVI,
                "MVA": this.cvssMVA,
                "MSC": this.cvssMSC,
                "MSI": this.cvssMSI,
                "MSA": this.cvssMSA,
                "CR": this.cvssCR,
                "IR": this.cvssIR,
                "AR": this.cvssAR,
                "E": this.cvssE,
            };

            const metrics = calculateCVSSv4MetricsFromVector(this.vector);
            if (!metrics) return;

            this.updateMetrics(
                metrics["AV"],
                metrics["AC"],
                metrics["AT"],
                metrics["PR"],
                metrics["UI"],
                metrics["VC"],
                metrics["VI"],
                metrics["VA"],
                metrics["SC"],
                metrics["SI"],
                metrics["SA"],
                metrics["S"],
                metrics["AU"],
                metrics["R"],
                metrics["V"],
                metrics["RE"],
                metrics["U"],
                metrics["MAV"],
                metrics["MAC"],
                metrics["MAT"],
                metrics["MPR"],
                metrics["MUI"],
                metrics["MVC"],
                metrics["MVI"],
                metrics["MVA"],
                metrics["MSC"],
                metrics["MSI"],
                metrics["MSA"],
                metrics["CR"],
                metrics["IR"],
                metrics["AR"],
                metrics["E"]
            )
            const {
                success,
                baseScore,
                qualScore,
                scoreType,
                vectorString,
            } = calculateCVSSv4ScoreFromMetrics(metrics);

            if (success) {
                this.updateScoreVector(
                    baseScore,
                    qualScore,
                    scoreType,
                    vectorString,
                );
            }
        }
    },
    template: `
  <!--

  Copyright (c) 2019, FIRST.ORG, INC.
  All rights reserved.

  Redistribution and use in source and binary forms, with or without modification, are permitted provided that the
  following conditions are met:
  1. Redistributions of source code must retain the above copyright notice, this list of conditions and the following
     disclaimer.
  2. Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the
     following disclaimer in the documentation and/or other materials provided with the distribution.
  3. Neither the name of the copyright holder nor the names of its contributors may be used to endorse or promote
     products derived from this software without specific prior written permission.

  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES,
  INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
  DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
  SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
  SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
  OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
-->
    <div class="cvss-calculator">

    <div v-bind:style="{ height: chartHeight*1.05, paddingBottom: '0.25rem' }">
        <apexchart ref="cvss-v4-chart-2" :type="chartType" :options="chartOptions" :series="chartSeries"
            :height="chartHeight" />
    </div>

    <div v-bind:style="{ height: chartBarHeight*1.05, paddingBottom: '0.25rem' }">
        <apexchart ref="cvss-v4-chart-1" type="bar" :options="chartBarOptions" :series="chartBarSeries"
            :height="chartBarHeight" />
    </div>

    <div class="cvss-score-rating">
        <p v-if="!baseScore" class="alert alert-dark p-2 m-2">
            Select values for all base metrics to generate score
        </p>
        <span id="CVSS_v4_cvss-base-metric-core"></span>
        <span id="CVSS_v4_cvss-base-severity"></span>
    </div>

    <dl class="cvss-fieldset cvss-score-rating">
        <dt id="CVSS_v4_score_type" class="col-sm-3">{{ scoreType ?? "CVSS-B" }} Score: </dt>
        <dd class="col-sm-9">
            <span id="CVSS_v4_score" v-bind:class="[badgeClass, CVSSColour(baseScore)]">{{ baseScore ?? "-" }} / {{ qualScore ?? "-" }}</span>
        </dd>

        <dt class="col-sm-3">Vector: </dt>
        <dd class="col-sm-9">
            <input id="CVSS_v4_vector" :disabled="true" class="form-control w-100" type="text" v-model="vector" @change="handleVectorChange" />
        </dd>
    </dl>


    <fieldset id="CVSS_v4_baseMetricGroup" class="cvss-fieldset">
        <legend class="font-weight-bold" id="CVSS_v4_baseMetricGroup_Legend"
            title="This metric reflects the context by which vulnerability exploitation is possible. This metric value (and consequently the resulting severity) will be larger the more remote (logically, and physically) an attacker can be in order to exploit the vulnerable system. The assumption is that the number of potential attackers for a vulnerability that could be exploited from across a network is larger than the number of potential attackers that could exploit a vulnerability requiring physical access to a device, and therefore warrants a greater severity.">
            Base Score</legend>

        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v4_AV_Heading"
                    title="This metric reflects the context by which vulnerability exploitation is possible. This metric value (and consequently the resulting severity) will be larger the more remote (logically, and physically) an attacker can be in order to exploit the vulnerable system. The assumption is that the number of potential attackers for a vulnerability that could be exploited from across a network is larger than the number of potential attackers that could exploit a vulnerability requiring physical access to a device, and therefore warrants a greater severity.">
                    Attack Vector (AV)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V4_AV" value="N" id="CVSS_v4_AV_N" type="radio" /><label
                    for="CVSS_v4_AV_N" id="CVSS_v4_AV_N_Label"
                    title="The vulnerable system is bound to the network stack and the set of possible attackers extends beyond the other options listed below, up to and including the entire Internet. Such a vulnerability is often termed 'remotely exploitable' and can be thought of as an attack being exploitable at the protocol level one or more network hops away (e.g., across one or more routers). An example of a network attack is an attacker causing a denial of service (DoS) by sending a specially crafted TCP packet across a wide area network (e.g., CVE-2004-0230).">Network
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V4_AV" value="A" id="CVSS_v4_AV_A" type="radio" /><label
                    for="CVSS_v4_AV_A" id="CVSS_v4_AV_A_Label"
                    title="The vulnerable system is bound to a protocol stack, but the attack is limited at the protocol level to a logically adjacent topology. This can mean an attack must be launched from the same shared proximity (e.g., Bluetooth, NFC, or IEEE 802.11) or logical network (e.g., local IP subnet), or from within a secure or otherwise limited administrative domain (e.g., MPLS, secure VPN within an administrative network zone). One example of an Adjacent attack would be an ARP (IPv4) or neighbor discovery (IPv6) flood leading to a denial of service on the local LAN segment (e.g., CVE-2013-6014).">Adjacent
                    (A)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V4_AV" value="L" id="CVSS_v4_AV_L" type="radio" /><label
                    for="CVSS_v4_AV_L" id="CVSS_v4_AV_L_Label"
                    title="The vulnerable system is not bound to the network stack and the attacker’s path is via read/write/execute capabilities. Either: the attacker exploits the vulnerability by accessing the target system locally (e.g., keyboard, console), or through terminal emulation (e.g., SSH); or the attacker relies on User Interaction by another person to perform actions required to exploit the vulnerability (e.g., using social engineering techniques to trick a legitimate user into opening a malicious document).">Local
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V4_AV" value="P" id="CVSS_v4_AV_P" type="radio" /><label
                    for="CVSS_v4_AV_P" id="CVSS_v4_AV_P_Label"
                    title="The attack requires the attacker to physically touch or manipulate the vulnerable system. Physical interaction may be brief (e.g., evil maid attack1) or persistent. An example of such an attack is a cold boot attack in which an attacker gains access to disk encryption keys after physically accessing the target system. Other examples include peripheral attacks via FireWire/USB Direct Memory Access (DMA).">Physical
                    (P)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_AC_Heading"
                    title="This metric captures measurable actions that must be taken by the attacker to actively evade or circumvent existing built-in security-enhancing conditions in order to obtain a working exploit. These are conditions whose primary purpose is to increase security and/or increase exploit engineering complexity. A vulnerability exploitable without a target-specific variable has a lower complexity than a vulnerability that would require non-trivial customization. This metric is meant to capture security mechanisms utilized by the vulnerable system, and does not relate to the amount of time or attempts it would take for an attacker to succeed, e.g. a race condition. If the attacker does not take action to overcome these conditions, the attack will always fail.
                        \nThe evasion or satisfaction of authentication mechanisms or requisites is included in the Privileges Required assessment and is not considered here as a factor of relevance for Attack Complexity.">
                    Attack Complexity (AC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V4_AC" value="L" id="CVSS_v4_AC_L" type="radio" /><label
                    for="CVSS_v4_AC_L" id="CVSS_v4_AC_L_Label"
                    title="The attacker must take no measurable action to exploit the vulnerability. The attack requires no target-specific circumvention to exploit the vulnerability. An attacker can expect repeatable success against the vulnerable system.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V4_AC" value="H" id="CVSS_v4_AC_H" type="radio" /><label
                    for="CVSS_v4_AC_H" id="CVSS_v4_AC_H_Label"
                    title="The successful attack depends on the evasion or circumvention of security-enhancing techniques in place that would otherwise hinder the attack. These include: Evasion of exploit mitigation techniques. The attacker must have additional methods available to bypass security measures in place. For example, circumvention of address space randomization (ASLR) or data execution prevention (DEP) must be performed for the attack to be successful. Obtaining target-specific secrets. The attacker must gather some target-specific secret before the attack can be successful. A secret is any piece of information that cannot be obtained through any amount of reconnaissance. To obtain the secret the attacker must perform additional attacks or break otherwise secure measures (e.g. knowledge of a secret key may be needed to break a crypto channel). This operation must be performed for each attacked target.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_AT_Heading"
                    title="This metric captures the prerequisite deployment and execution conditions or variables of the vulnerable system that enable the attack. These differ from security-enhancing techniques/technologies (ref Attack Complexity) as the primary purpose of these conditions is not to explicitly mitigate attacks, but rather, emerge naturally as a consequence of the deployment and execution of the vulnerable system. If the attacker does not take action to overcome these conditions, the attack may succeed only occasionally or not succeed at all.">
                    Attack Requirements (AT)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AT" name="CVSS_V4_AT" value="N" id="CVSS_v4_AT_N" type="radio" /><label
                    for="CVSS_v4_AT_N" id="CVSS_v4_AT_N_Label"
                    title="The successful attack does not depend on the deployment and execution conditions of the vulnerable system. The attacker can expect to be able to reach the vulnerability and execute the exploit under all or most instances of the vulnerability.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AT" name="CVSS_V4_AT" value="P" id="CVSS_v4_AT_P" type="radio" /><label
                    for="CVSS_v4_AT_P" id="CVSS_v4_AT_P_Label"
                    title="The successful attack depends on the presence of specific deployment and execution conditions of the vulnerable system that enable the attack. These include: A race condition must be won to successfully exploit the vulnerability. The successfulness of the attack is conditioned on execution conditions that are not under full control of the attacker. The attack may need to be launched multiple times against a single target before being successful. Network injection. The attacker must inject themselves into the logical network path between the target and the resource requested by the victim (e.g. vulnerabilities requiring an on-path attacker).">Present
                    (P)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_PR_Heading"
                    title="This metric describes the level of privileges an attacker must possess prior to successfully exploiting the vulnerability. The method by which the attacker obtains privileged credentials prior to the attack (e.g., free trial accounts), is outside the scope of this metric. Generally, self-service provisioned accounts do not constitute a privilege requirement if the attacker can grant themselves privileges as part of the attack.
                            \nThe resulting score is greatest if no privileges are required.">
                    Privileges Required (PR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="PR" name="CVSS_V4_PR" value="N" id="CVSS_v4_PR_N" type="radio" /><label
                    for="CVSS_v4_PR_N" id="CVSS_v4_PR_N_Label"
                    title="The attacker is unauthenticated prior to attack, and therefore does not require any access to settings or files of the vulnerable system to carry out an attack.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="PR" name="CVSS_V4_PR" value="L" id="CVSS_v4_PR_L" type="radio" /><label
                    for="CVSS_v4_PR_L" id="CVSS_v4_PR_L_Label"
                    title="The attacker requires privileges that provide basic capabilities that are typically limited to settings and resources owned by a single low-privileged user. Alternatively, an attacker with Low privileges has the ability to access only non-sensitive resources.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="PR" name="CVSS_V4_PR" value="H" id="CVSS_v4_PR_H" type="radio" /><label
                    for="CVSS_v4_PR_H" id="CVSS_v4_PR_H_Label"
                    title="The attacker requires privileges that provide significant (e.g., administrative) control over the vulnerable system allowing full access to the vulnerable system’s settings and files.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_UI_Heading"
                    title="This metric captures the requirement for a human user, other than the attacker, to participate in the successful compromise of the vulnerable system. This metric determines whether the vulnerability can be exploited solely at the will of the attacker, or whether a separate user (or user-initiated process) must participate in some manner. The resulting score is greatest when no user interaction is required.">
                    User Interaction (UI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="UI" name="CVSS_V4_UI" value="N" id="CVSS_v4_UI_N" type="radio" /><label
                    for="CVSS_v4_UI_N" id="CVSS_v4_UI_N_Label"
                    title="The vulnerable system can be exploited without interaction from any human user, other than the attacker. Examples include: a remote attacker is able to send packets to a target system a locally authenticated attacker executes code to elevate privileges.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="UI" name="CVSS_V4_UI" value="P" id="CVSS_v4_UI_P" type="radio" /><label
                    for="CVSS_v4_UI_P" id="CVSS_v4_UI_P_Label"
                    title="Successful exploitation of this vulnerability requires limited interaction by the targeted user with the vulnerable system and the attacker’s payload. These interactions would be considered involuntary and do not require that the user actively subvert protections built into the vulnerable system. Examples include: utilizing a website that has been modified to display malicious content when the page is rendered (most stored XSS or CSRF) running an application that calls a malicious binary that has been planted on the system using an application which generates traffic over an untrusted or compromised network (vulnerabilities requiring an on-path attacker).">Passive
                    (P)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="UI" name="CVSS_V4_UI" value="A" id="CVSS_v4_UI_A" type="radio" /><label
                    for="CVSS_v4_UI_A" id="CVSS_v4_UI_A_Label"
                    title="Successful exploitation of this vulnerability requires a targeted user to perform specific, conscious interactions with the vulnerable system and the attacker’s payload, or the user’s interactions would actively subvert protection mechanisms which would lead to exploitation of the vulnerability. Examples include: importing a file into a vulnerable system in a specific manner placing files into a specific directory prior to executing code submitting a specific string into a web application (e.g. reflected or self XSS) dismiss or accept prompts or security warnings prior to taking an action (e.g. opening/editing a file, connecting a device).">Active
                    (A)</label>
            </div>

        </div>


        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v4_VC_Heading"
                    title="The Impact metrics capture the effects of a successfully exploited vulnerability. Analysts should constrain impacts to a reasonable, final outcome which they are confident an attacker is able to achieve.
                    \nOnly an increase in access, privileges gained, or other negative outcome as a result of successful exploitation should be considered when assessing the Impact metrics of a vulnerability. For example, consider a vulnerability that requires read-only permissions prior to being able to exploit the vulnerability. After successful exploitation, the attacker maintains the same level of read access, and gains write access. In this case, only the Integrity impact metric should be scored, and the Confidentiality and Availability Impact metrics should be set as None.
                    \nNote that when scoring a delta change in impact, the final impact should be used. For example, if an attacker starts with partial access to restricted information (Confidentiality Low) and successful exploitation of the vulnerability results in complete loss in confidentiality (Confidentiality High), then the resultant CVSS Base metric value should reference the 'end game' Impact metric value (Confidentiality High).
                    \nWhen identifying values for the impact metrics, assessment providers need to account for impacts both to the Vulnerable System and impacts outside of the Vulnerable System. These impacts are established by two sets of impact metrics: 'Vulnerable System impact' and 'Subsequent System impact'. When establishing the boundaries for the Vulnerable System metric values, assessment providers should use the conceptual model of a system of interest.
                    \nFormally, a system of interest for scoring a vulnerability is defined as the set of computing logic that executes in an environment with a coherent function and set of security policies. The vulnerability exists in one or more components of such a system. A technology product or a solution that serves a purpose or function from a consumer's perspective is considered a system (e.g., a server, workstation, containerized service, etc.).
                    \nWhen a system provides its functionality solely to another system, or it is designed to be exclusively used by another system, then together they are considered as the system of interest for scoring. For example, a database used solely by a smart speaker is considered a part of that smart speaker system. Both the database and the smart speaker it serves would be considered the vulnerable system if a vulnerability in that database leads to the malfunction of the smart speaker. When a vulnerability does not have impact outside of the vulnerable system assessment providers should leave the subsequent system impact metrics as NONE (N).
                    \nAll impacts, if any, that occur outside of the vulnerable system should be reflected in the subsequent system impact set. When assessed in the environmental metric group only, the subsequent system impact may, in addition to the logical systems defined for System of Interest, also include impacts to humans. This human impact option in the environmental metric group is explained further in Safety (S), below.">
                    Vulnerable System Confidentiality (VC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="VC" name="CVSS_V4_VC" value="H" id="CVSS_v4_VC_H" type="radio" /><label for="CVSS_v4_VC_H"
                    id="CVSS_v4_VC_H_Label"
                    title="There is a total loss of confidentiality, resulting in all information within the Vulnerable System being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact. For example, an attacker steals the administrator's password, or private encryption keys of a web server.">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="VC" name="CVSS_V4_VC" value="L" id="CVSS_v4_VC_L" type="radio" /><label for="CVSS_v4_VC_L"
                    id="CVSS_v4_VC_L_Label"
                    title="	There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the Vulnerable System.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="VC" name="CVSS_V4_VC" value="N" id="CVSS_v4_VC_N" type="radio" /><label for="CVSS_v4_VC_N"
                    id="CVSS_v4_VC_N_Label" title="There is no loss of confidentiality within the Vulnerable System.">None
                    (N)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_VI_Heading"
                    title="This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information. Integrity of a system is impacted when an attacker causes unauthorized modification of system data. Integrity is also impacted when a system user can repudiate critical actions taken in the context of the system (e.g. due to insufficient logging).
                        \nThe resulting score is greatest when the consequence to the system is highest.">
                    Vulnerable System Integrity (VI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="VI" name="CVSS_V4_VI" value="H" id="CVSS_v4_VI_H" type="radio" /><label for="CVSS_v4_VI_H"
                    id="CVSS_v4_VI_H_Label"
                    title="There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the Vulnerable System. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the Vulnerable System.">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="VI" name="CVSS_V4_VI" value="L" id="CVSS_v4_VI_L" type="radio" /><label for="CVSS_v4_VI_L"
                    id="CVSS_v4_VI_L_Label"
                    title="Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact to the Vulnerable System.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="VI" name="CVSS_V4_VI" value="N" id="CVSS_v4_VI_N" type="radio" /><label for="CVSS_v4_VI_N"
                    id="CVSS_v4_VI_N_Label" title="There is no loss of integrity within the Vulnerable System.">None (N)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_VA_Heading"
                    title="This metric measures the impact to the availability of the impacted system resulting from a successfully exploited vulnerability. While the Confidentiality and Integrity impact metrics apply to the loss of confidentiality or integrity of data (e.g., information, files) used by the system, this metric refers to the loss of availability of the impacted system itself, such as a networked service (e.g., web, database, email). Since availability refers to the accessibility of information resources, attacks that consume network bandwidth, processor cycles, or disk space all impact the availability of a system. The resulting score is greatest when the consequence to the system is highest.">
                    Vulnerable System Availability (VA)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="VA" name="CVSS_V4_VA" value="H" id="CVSS_v4_VA_H" type="radio" /><label for="CVSS_v4_VA_H"
                    id="CVSS_v4_VA_H_Label"
                    title="There is a total loss of availability, resulting in the attacker being able to fully deny access to resources in the Vulnerable System; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the Vulnerable System (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable).">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="VA" name="CVSS_V4_VA" value="L" id="CVSS_v4_VA_L" type="radio" /><label for="CVSS_v4_VA_L"
                    id="CVSS_v4_VA_L_Label"
                    title="Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the Vulnerable System are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the Vulnerable System.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="VA" name="CVSS_V4_VA" value="N" id="CVSS_v4_VA_N" type="radio" /><label for="CVSS_v4_VA_N"
                    id="CVSS_v4_VA_N_Label" title="There is no impact to availability within the Vulnerable System.">None
                    (N)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_SC_Heading"
                title="The Impact metrics capture the effects of a successfully exploited vulnerability. Analysts should constrain impacts to a reasonable, final outcome which they are confident an attacker is able to achieve.
                \nOnly an increase in access, privileges gained, or other negative outcome as a result of successful exploitation should be considered when assessing the Impact metrics of a vulnerability. For example, consider a vulnerability that requires read-only permissions prior to being able to exploit the vulnerability. After successful exploitation, the attacker maintains the same level of read access, and gains write access. In this case, only the Integrity impact metric should be scored, and the Confidentiality and Availability Impact metrics should be set as None.
                \nNote that when scoring a delta change in impact, the final impact should be used. For example, if an attacker starts with partial access to restricted information (Confidentiality Low) and successful exploitation of the vulnerability results in complete loss in confidentiality (Confidentiality High), then the resultant CVSS Base metric value should reference the 'end game' Impact metric value (Confidentiality High).
                \nWhen identifying values for the impact metrics, assessment providers need to account for impacts both to the Vulnerable System and impacts outside of the Vulnerable System. These impacts are established by two sets of impact metrics: 'Vulnerable System impact' and 'Subsequent System impact'. When establishing the boundaries for the Vulnerable System metric values, assessment providers should use the conceptual model of a system of interest.
                \nFormally, a system of interest for scoring a vulnerability is defined as the set of computing logic that executes in an environment with a coherent function and set of security policies. The vulnerability exists in one or more components of such a system. A technology product or a solution that serves a purpose or function from a consumer's perspective is considered a system (e.g., a server, workstation, containerized service, etc.).
                \nWhen a system provides its functionality solely to another system, or it is designed to be exclusively used by another system, then together they are considered as the system of interest for scoring. For example, a database used solely by a smart speaker is considered a part of that smart speaker system. Both the database and the smart speaker it serves would be considered the vulnerable system if a vulnerability in that database leads to the malfunction of the smart speaker. When a vulnerability does not have impact outside of the vulnerable system assessment providers should leave the subsequent system impact metrics as NONE (N).
                \nAll impacts, if any, that occur outside of the vulnerable system should be reflected in the subsequent system impact set. When assessed in the environmental metric group only, the subsequent system impact may, in addition to the logical systems defined for System of Interest, also include impacts to humans. This human impact option in the environmental metric group is explained further in Safety (S), below.">
                    Subsequent System Confidentiality (SC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="SC" name="CVSS_V4_SC" value="H" id="CVSS_v4_SC_H" type="radio" /><label for="CVSS_v4_SC_H"
                    id="CVSS_v4_SC_H_Label"
                    title="There is a total loss of confidentiality, resulting in all resources within the Subsequent System being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact. For example, an attacker steals the administrator's password, or private encryption keys of a web server.">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="SC" name="CVSS_V4_SC" value="L" id="CVSS_v4_SC_L" type="radio" /><label for="CVSS_v4_SC_L"
                    id="CVSS_v4_SC_L_Label"
                    title="There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the Subsequent System.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="SC" name="CVSS_V4_SC" value="N" id="CVSS_v4_SC_N" type="radio" /><label for="CVSS_v4_SC_N"
                    id="CVSS_v4_SC_N_Label" title="There is no loss of confidentiality within the Subsequent System or all confidentiality impact is constrained to the Vulnerable System.">None
                    (N)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_SI_Heading"
                title="This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information. Integrity of a system is impacted when an attacker causes unauthorized modification of system data. Integrity is also impacted when a system user can repudiate critical actions taken in the context of the system (e.g. due to insufficient logging).
                \nThe resulting score is greatest when the consequence to the system is highest.">
                    Subsequent System Integrity (SI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="SI" name="CVSS_V4_SI" value="H" id="CVSS_v4_SI_H" type="radio" /><label for="CVSS_v4_SI_H"
                    id="CVSS_v4_SI_H_Label"
                    title="	There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the Subsequent System. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the Subsequent System.">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="SI" name="CVSS_V4_SI" value="L" id="CVSS_v4_SI_L" type="radio" /><label for="CVSS_v4_SI_L"
                    id="CVSS_v4_SI_L_Label"
                    title="Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact to the Subsequent System.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="SI" name="CVSS_V4_SI" value="N" id="CVSS_v4_SI_N" type="radio" /><label for="CVSS_v4_SI_N"
                    id="CVSS_v4_SI_N_Label" title="There is no loss of integrity within the Subsequent System or all integrity impact is constrained to the Vulnerable System.">None (N)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_SA_Heading"
                title="This metric measures the impact to the availability of the impacted system resulting from a successfully exploited vulnerability. While the Confidentiality and Integrity impact metrics apply to the loss of confidentiality or integrity of data (e.g., information, files) used by the system, this metric refers to the loss of availability of the impacted system itself, such as a networked service (e.g., web, database, email). Since availability refers to the accessibility of information resources, attacks that consume network bandwidth, processor cycles, or disk space all impact the availability of a system. The resulting score is greatest when the consequence to the system is highest.">
                    Subsequent System Availability (SA)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="SA" name="CVSS_V4_SA" value="H" id="CVSS_v4_SA_H" type="radio" /><label for="CVSS_v4_SA_H"
                    id="CVSS_v4_SA_H_Label"
                    title="There is a total loss of availability, resulting in the attacker being able to fully deny access to resources in the Subsequent System; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the Subsequent System (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable).">High
                    (H)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="SA" name="CVSS_V4_SA" value="L" id="CVSS_v4_SA_L" type="radio" /><label for="CVSS_v4_SA_L"
                    id="CVSS_v4_SA_L_Label"
                    title="Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the Subsequent System are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the Subsequent System.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="SA" name="CVSS_V4_SA" value="N" id="CVSS_v4_SA_N" type="radio" /><label for="CVSS_v4_SA_N"
                    id="CVSS_v4_SA_N_Label" title="There is no impact to availability within the Subsequent System or all availability impact is constrained to the Vulnerable System.">None
                    (N)</label>
            </div>

        </div>

    </fieldset>

    <fieldset id="CVSS_v4_supplementalMetricGroup" class="cvss-fieldset">
        <legend class="font-weight-bold" id="CVSS_v4_supplementalMetricGroup_Legend"
            title="A new, optional metric group called the Supplemental metric group provides new metrics that describe and measure additional extrinsic attributes of a vulnerability. While the assessment of Supplemental metrics is provisioned by the provider, the usage and response plan of each metric within the Supplemental metric group is determined by the consumer. This contextual information may be employed differently in each consumer’s environment. No metric will have any impact on the final calculated CVSS score (e.g. CVSS-BTE). Organizations may then assign importance and/or effective impact of each metric, or set/combination of metrics, giving them more, less, or absolutely no effect on the final risk analysis. Metrics and values will simply convey additional extrinsic characteristics of the vulnerability itself.">
            Supplemental Metrics</legend>
            <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v4_S_Heading"
                    title="Like all Supplemental Metrics, providing a value for Safety is completely optional. Suppliers and vendors (AKA: scoring providers) may or may not provide Safety as a Supplemental Metric as they see fit.
                    \nWhen a system does have an intended use or fitness of purpose aligned to safety, it is possible that exploiting a vulnerability within that system may have Safety impact which can be represented in the Supplemental Metrics group. Lack of a Safety metric value being supplied does NOT mean that there may not be any Safety-related impacts.">
                    Safety (S)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="S" name="CVSS_V4_S" value="X" id="CVSS_v4_S_X" type="radio" /><label
                    for="CVSS_v4_S_X" id="CVSS_v4_S_X_Label"
                    title="The metric has not been evaluated.">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="S" name="CVSS_V4_S" value="N" id="CVSS_v4_S_N" type="radio" /><label
                    for="CVSS_v4_S_N" id="CVSS_v4_S_N_Label"
                    title="Consequences of the vulnerability meet definition of IEC 61508 consequence category 'negligible'">Negligible
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="S" name="CVSS_V4_S" value="P" id="CVSS_v4_S_P" type="radio" /><label
                    for="CVSS_v4_S_P" id="CVSS_v4_S_P_Label"
                    title="Consequences of the vulnerability meet definition of IEC 61508 consequence categories of 'marginal,' 'critical,' or 'catastrophic'.">Present
                    (P)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_AU_Heading"
                    title="The 'Automatable' metric captures the answer to the question 'Can an attacker automate exploitation events for this vulnerability across multiple targets?' based on steps 1-4 of the kill chain2 [Hutchins et al., 2011]. These steps are reconnaissance, weaponization, delivery, and exploitation. If evaluated, the metric can take the values no or yes.">
                    Automatable (AU)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AU" name="CVSS_V4_AU" value="X" id="CVSS_v4_AU_X" type="radio" /><label
                    for="CVSS_v4_AU_X" id="CVSS_v4_AU_X_Label"
                    title="The metric has not been evaluated.">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AU" name="CVSS_V4_AU" value="N" id="CVSS_v4_AU_N" type="radio" /><label
                    for="CVSS_v4_AU_N" id="CVSS_v4_AU_N_Label"
                    title="Attackers cannot reliably automate all 4 steps of the kill chain for this vulnerability for some reason. These steps are reconnaissance, weaponization, delivery, and exploitation.">No
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AU" name="CVSS_V4_AU" value="Y" id="CVSS_v4_AU_Y" type="radio" /><label
                    for="CVSS_v4_AU_Y" id="CVSS_v4_AU_Y_Label"
                    title="Attackers can reliably automate all 4 steps of the kill chain. These steps are reconnaissance, weaponization, delivery, and exploitation (e.g., the vulnerability is 'wormable').">Yes
                    (Y)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_R_Heading"
                    title="Recovery describes the resilience of a system to recover services, in terms of performance and availability, after an attack has been performed.">
                    Recovery (R)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="R" name="CVSS_V4_R" value="X" id="CVSS_v4_R_X" type="radio" /><label
                    for="CVSS_v4_R_X" id="CVSS_v4_R_X_Label"
                    title="The metric has not been evaluated.">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="R" name="CVSS_V4_R" value="A" id="CVSS_v4_R_A" type="radio" /><label
                    for="CVSS_v4_R_A" id="CVSS_v4_R_A_Label"
                    title="The system recovers services automatically after an attack has been performed.">Automatic
                    (A)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="R" name="CVSS_V4_R" value="U" id="CVSS_v4_R_U" type="radio" /><label
                    for="CVSS_v4_R_U" id="CVSS_v4_R_U_Label"
                    title="The system requires manual intervention by the user to recover services, after an attack has been performed.">User
                    (U)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="R" name="CVSS_V4_R" value="I" id="CVSS_v4_R_I" type="radio" /><label
                    for="CVSS_v4_R_I" id="CVSS_v4_R_I_Label"
                    title="The system services are irrecoverable by the user, after an attack has been performed.">Irrecoverable
                    (I)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_V_Heading"
                    title="Value Density describes the resources that the attacker will gain control over with a single exploitation event. It has two possible values, diffuse and concentrated.">
                    Value Density (V)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="V" name="CVSS_V4_V" value="X" id="CVSS_v4_V_X" type="radio" /><label
                    for="CVSS_v4_V_X" id="CVSS_v4_V_X_Label"
                    title="The metric has not been evaluated.">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="V" name="CVSS_V4_V" value="D" id="CVSS_v4_V_D" type="radio" /><label
                    for="CVSS_v4_V_D" id="CVSS_v4_V_D_Label"
                    title="The vulnerable system has limited resources. That is, the resources that the attacker will gain control over with a single exploitation event are relatively small. An example of Diffuse (think: limited) Value Density would be an attack on a single email client vulnerability.">Diffuse
                    (D)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="V" name="CVSS_V4_V" value="C" id="CVSS_v4_V_C" type="radio" /><label
                    for="CVSS_v4_V_C" id="CVSS_v4_V_C_Label"
                    title="The vulnerable system is rich in resources. Heuristically, such systems are often the direct responsibility of 'system operators' rather than users. An example of Concentrated (think: broad) Value Density would be an attack on a central email server.">Concentrated
                    (C)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_RE_Heading"
                    title="The intention of the Vulnerability Response Effort metric is to provide supplemental information on how difficult it is for consumers to provide an initial response to the impact of vulnerabilities for deployed products and services in their infrastructure. The consumer can then take this additional information on effort required into consideration when applying mitigations and/or scheduling remediation.
                        \nWhen calculating Vulnerability Response Effort, the effort required to deploy the quickest available response should be considered.">
                    Vulnerability Response Effort (RE)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="RE" name="CVSS_V4_RE" value="X" id="CVSS_v4_RE_X" type="radio" /><label
                    for="CVSS_v4_RE_X" id="CVSS_v4_RE_X_Label"
                    title="The metric has not been evaluated.">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RE" name="CVSS_V4_RE" value="L" id="CVSS_v4_RE_L" type="radio" /><label
                    for="CVSS_v4_RE_L" id="CVSS_v4_RE_L_Label"
                    title="The effort required to respond to a vulnerability is low/trivial. Examples include: communication on better documentation, configuration workarounds, or guidance from the vendor that does not require an immediate update, upgrade, or replacement by the consuming entity, such as firewall filter configuration.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RE" name="CVSS_V4_RE" value="M" id="CVSS_v4_RE_M" type="radio" /><label
                    for="CVSS_v4_RE_M" id="CVSS_v4_RE_M_Label"
                    title="The actions required to respond to a vulnerability require some effort on behalf of the consumer and could cause minimal service impact to implement. Examples include: simple remote update, disabling of a subsystem, or a low-touch software upgrade such as a driver update.">Moderate
                    (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RE" name="CVSS_V4_RE" value="H" id="CVSS_v4_RE_H" type="radio" /><label
                    for="CVSS_v4_RE_H" id="CVSS_v4_RE_H_Label"
                    title="The actions required to respond to a vulnerability are significant and/or difficult, and may possibly lead to an extended, scheduled service impact. This would need to be considered for scheduling purposes including honoring any embargo on deployment of the selected response. Alternatively, response to the vulnerability in the field is not possible remotely. The only resolution to the vulnerability involves physical replacement (e.g. units deployed would have to be recalled for a depot level repair or replacement). Examples include: a highly privileged driver update, microcode or UEFI BIOS updates, or software upgrades requiring careful analysis and understanding of any potential infrastructure impact before implementation. A UEFI BIOS update that impacts Trusted Platform Module (TPM) attestation without impacting disk encryption software such as Bit locker is a good recent example. Irreparable failures such as non-bootable flash subsystems, failed disks or solid-state drives (SSD), bad memory modules, network devices, or other non-recoverable under warranty hardware, should also be scored as having a High effort.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_U_Heading"
                    title="Many vendors currently provide supplemental severity ratings to consumers via product security advisories. Other vendors publish Qualitative Severity Ratings from the CVSS Specification Document in their advisories.
                        \nTo facilitate a standardized method to incorporate additional provider-supplied assessment, an optional 'pass-through' Supplemental Metric called Provider Urgency is available.
                        \nThe Penultimate Product Provider (PPP) is best positioned to provide a direct assessment of Provider Urgency.">
                    Provider Urgency (U)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="U" name="CVSS_V4_U" value="X" id="CVSS_v4_U_X" type="radio" /><label
                    for="CVSS_v4_U_X" id="CVSS_v4_U_X_Label"
                    title="The metric has not been evaluated.">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="U" name="CVSS_V4_U" value="Clear" id="CVSS_v4_U_Clear" type="radio" /><label
                    for="CVSS_v4_U_Clear" id="CVSS_v4_U_Clear_Label"
                    title="Provider has assessed the impact of this vulnerability as having no urgency (Informational).">Clear
                    (Clear)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="U" name="CVSS_V4_U" value="Green" id="CVSS_v4_U_Green" type="radio" /><label
                    for="CVSS_v4_U_Green" id="CVSS_v4_U_Green_Label"
                    title="Provider has assessed the impact of this vulnerability as having a reduced urgency.">Green
                    (Green)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="U" name="CVSS_V4_U" value="Amber" id="CVSS_v4_U_Amber" type="radio" /><label
                    for="CVSS_v4_U_Amber" id="CVSS_v4_U_Amber_Label"
                    title="Provider has assessed the impact of this vulnerability as having a moderate urgency.">Amber
                    (Amber)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="U" name="CVSS_V4_U" value="Red" id="CVSS_v4_U_Red" type="radio" /><label
                    for="CVSS_v4_U_Red" id="CVSS_v4_U_Red_Label"
                    title="Provider has assessed the impact of this vulnerability as having the highest urgency.">Red
                    (Red)</label>
            </div>

            

        </div>
    </fieldset>


    <fieldset id="CVSS_v4_modifiedBaseMetricGroup" class="cvss-fieldset">
        <legend class="font-weight-bold" id="CVSS_v4_baseMetricGroup_Legend"
            title="The Base Metric group represents the intrinsic  characteristics of a vulnerability that are constant over time and across user environments. Determine the vulnerable component and score Attack Vector, Attack Complexity, Privileges Required and User Interaction relative to this.">
            Environmental (Modified Base Metrics) Score</legend>

        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v4_MAV_Heading"
                    title="This metric reflects the context by which vulnerability exploitation is possible. The Base Score increases the more remote (logically, and physically) an attacker can be in order to exploit the vulnerable component.">
                    Attack Vector (MAV)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V4_MAV" value="X" id="CVSS_v4_MAV_X" type="radio" /><label
                    for="CVSS_v4_MAV_X" id="CVSS_v4_MAV_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V4_MAV" value="N" id="CVSS_v4_MAV_N" type="radio" /><label
                    for="CVSS_v4_MAV_N" id="CVSS_v4_MAV_N_Label"
                    title="See base metric description.">Network
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V4_MAV" value="A" id="CVSS_v4_MAV_A" type="radio" /><label
                    for="CVSS_v4_MAV_A" id="CVSS_v4_MAV_A_Label"
                    title="See base metric description.">Adjacent
                    (A)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V4_MAV" value="L" id="CVSS_v4_MAV_L" type="radio" /><label
                    for="CVSS_v4_MAV_L" id="CVSS_v4_MAV_L_Label"
                    title="See base metric description.">Local
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V4_MAV" value="P" id="CVSS_v4_MAV_P" type="radio" /><label
                    for="CVSS_v4_MAV_P" id="CVSS_v4_MAV_P_Label"
                    title="See base metric description.">Physical
                    (P)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MAC_Heading"
                    title="See base metric description.">
                    Attack Complexity (MAC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAC" name="CVSS_V4_MAC" value="X" id="CVSS_v4_MAC_X" type="radio" /><label
                    for="CVSS_v4_MAC_X" id="CVSS_v4_MAC_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAC" name="CVSS_V4_MAC" value="L" id="CVSS_v4_MAC_L" type="radio" /><label
                    for="CVSS_v4_MAC_L" id="CVSS_v4_MAC_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAC" name="CVSS_V4_MAC" value="H" id="CVSS_v4_MAC_H" type="radio" /><label
                    for="CVSS_v4_MAC_H" id="CVSS_v4_MAC_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MAT_Heading"
                    title="See base metric description.">
                    Attack Requirements (MAT)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAT" name="CVSS_V4_MAT" value="X" id="CVSS_v4_MAT_X" type="radio" /><label
                    for="CVSS_v4_MAT_X" id="CVSS_v4_MAT_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAT" name="CVSS_V4_MAT" value="N" id="CVSS_v4_MAT_N" type="radio" /><label
                    for="CVSS_v4_MAT_N" id="CVSS_v4_MAT_N_Label"
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAT" name="CVSS_V4_MAT" value="P" id="CVSS_v4_MAT_P" type="radio" /><label
                    for="CVSS_v4_MAT_P" id="CVSS_v4_MAT_P_Label"
                    title="See base metric description.">Present
                    (P)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MPR_Heading"
                    title="See base metric description.">
                    Privileges Required (MPR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V4_MPR" value="X" id="CVSS_v4_MPR_X" type="radio" /><label
                    for="CVSS_v4_MPR_X" id="CVSS_v4_MPR_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V4_MPR" value="N" id="CVSS_v4_MPR_N" type="radio" /><label
                    for="CVSS_v4_MPR_N" id="CVSS_v4_MPR_N_Label"
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V4_MPR" value="L" id="CVSS_v4_MPR_L" type="radio" /><label
                    for="CVSS_v4_MPR_L" id="CVSS_v4_MPR_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V4_MPR" value="H" id="CVSS_v4_MPR_H" type="radio" /><label
                    for="CVSS_v4_MPR_H" id="CVSS_v4_MPR_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MUI_Heading"
                title="See base metric description.">
                    User Interaction (MUI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V4_MUI" value="X" id="CVSS_v4_MUI_X" type="radio" /><label
                    for="CVSS_v4_MUI_X" id="CVSS_v4_MUI_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V4_MUI" value="N" id="CVSS_v4_MUI_N" type="radio" /><label
                    for="CVSS_v4_MUI_N" id="CVSS_v4_MUI_N_Label"
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V4_MUI" value="P" id="CVSS_v4_MUI_P" type="radio" /><label
                    for="CVSS_v4_MUI_P" id="CVSS_v4_MUI_P_Label"
                    title="See base metric description.">Passive
                    (P)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V4_MUI" value="A" id="CVSS_v4_MUI_A" type="radio" /><label
                    for="CVSS_v4_MUI_A" id="CVSS_v4_MUI_A_Label"
                    title="See base metric description.">Active
                    (A)</label>
            </div>

        </div>


        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v4_MVC_Heading"
                    title="See base metric description.">
                    Vulnerable System Confidentiality (MVC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVC" name="CVSS_V4_MVC" value="X" id="CVSS_v4_MVC_X" type="radio" /><label
                    for="CVSS_v4_MVC_X" id="CVSS_v4_MVC_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVC" name="CVSS_V4_MVC" value="N" id="CVSS_v4_MVC_N" type="radio" /><label for="CVSS_v4_MVC_N"
                    id="CVSS_v4_MVC_N_Label"
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVC" name="CVSS_V4_MVC" value="L" id="CVSS_v4_MVC_L" type="radio" /><label for="CVSS_v4_MVC_L"
                    id="CVSS_v4_MVC_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVC" name="CVSS_V4_MVC" value="H" id="CVSS_v4_MVC_H" type="radio" /><label for="CVSS_v4_MVC_H"
                    id="CVSS_v4_MVC_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MVI_Heading"
                    title="See base metric description.">
                    Vulnerable System Integrity (MVI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVI" name="CVSS_V4_MVI" value="X" id="CVSS_v4_MVI_X" type="radio" /><label
                    for="CVSS_v4_MVI_X" id="CVSS_v4_MVI_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVI" name="CVSS_V4_MVI" value="N" id="CVSS_v4_MVI_N" type="radio" /><label for="CVSS_v4_MVI_N"
                    id="CVSS_v4_MVI_N_Label"
                    title="See base metric description.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVI" name="CVSS_V4_MVI" value="L" id="CVSS_v4_MVI_L" type="radio" /><label for="CVSS_v4_MVI_L"
                    id="CVSS_v4_MVI_L_Label"
                    ttitle="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVI" name="CVSS_V4_MVI" value="H" id="CVSS_v4_MVI_H" type="radio" /><label for="CVSS_v4_MVI_H"
                    id="CVSS_v4_MVI_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MVA_Heading"
                    title="See base metric description.">
                    Vulnerable System Availability (MVA)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVA" name="CVSS_V4_MVA" value="X" id="CVSS_v4_MVA_X" type="radio" /><label
                    for="CVSS_v4_MVA_X" id="CVSS_v4_MVA_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVA" name="CVSS_V4_MVA" value="N" id="CVSS_v4_MVA_N" type="radio" /><label for="CVSS_v4_MVA_N"
                    id="CVSS_v4_MVA_N_Label" 
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVA" name="CVSS_V4_MVA" value="L" id="CVSS_v4_MVA_L" type="radio" /><label for="CVSS_v4_MVA_L"
                    id="CVSS_v4_MVA_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MVA" name="CVSS_V4_MVA" value="H" id="CVSS_v4_MVA_H" type="radio" /><label for="CVSS_v4_MVA_H"
                    id="CVSS_v4_MVA_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MSC_Heading"
                title="See base metric description.">
                    Subsequent System Confidentiality (MSC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSC" name="CVSS_V4_MSC" value="X" id="CVSS_v4_MSC_X" type="radio" /><label
                    for="CVSS_v4_MSC_X" id="CVSS_v4_MSC_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSC" name="CVSS_V4_MSC" value="N" id="CVSS_v4_MSC_N" type="radio" /><label for="CVSS_v4_MSC_N"
                    id="CVSS_v4_MSC_N_Label" 
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSC" name="CVSS_V4_MSC" value="L" id="CVSS_v4_MSC_L" type="radio" /><label for="CVSS_v4_MSC_L"
                    id="CVSS_v4_MSC_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSC" name="CVSS_V4_MSC" value="H" id="CVSS_v4_MSC_H" type="radio" /><label for="CVSS_v4_MSC_H"
                    id="CVSS_v4_MSC_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MSI_Heading"
                title="See base metric description.">
                    Subsequent System Integrity (MSI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSI" name="CVSS_V4_MSI" value="X" id="CVSS_v4_MSI_X" type="radio" /><label
                    for="CVSS_v4_MSI_X" id="CVSS_v4_MSI_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSI" name="CVSS_V4_MSI" value="N" id="CVSS_v4_MSI_N" type="radio" /><label for="CVSS_v4_MSI_N"
                    id="CVSS_v4_MSI_N_Label" 
                    title="See base metric description.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSI" name="CVSS_V4_MSI" value="L" id="CVSS_v4_MSI_L" type="radio" /><label for="CVSS_v4_MSI_L"
                    id="CVSS_v4_MSI_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSI" name="CVSS_V4_MSI" value="H" id="CVSS_v4_MSI_H" type="radio" /><label for="CVSS_v4_MSI_H"
                    id="CVSS_v4_MSI_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_MSA_Heading"
                    title="See base metric description.">
                    Subsequent System Availability (MSA)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSA" name="CVSS_V4_MSA" value="X" id="CVSS_v4_MSA_X" type="radio" /><label
                    for="CVSS_v4_MSA_X" id="CVSS_v4_MSA_X_Label"
                    title="Not Defined">Not Defined
                    (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSA" name="CVSS_V4_MSA" value="N" id="CVSS_v4_MSA_N" type="radio" /><label for="CVSS_v4_MSA_N"
                    id="CVSS_v4_MSA_N_Label" 
                    title="See base metric description.">None
                    (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSA" name="CVSS_V4_MSA" value="L" id="CVSS_v4_MSA_L" type="radio" /><label for="CVSS_v4_MSA_L"
                    id="CVSS_v4_MSA_L_Label"
                    title="See base metric description.">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MSA" name="CVSS_V4_MSA" value="H" id="CVSS_v4_MSA_H" type="radio" /><label for="CVSS_v4_MSA_H"
                    id="CVSS_v4_MSA_H_Label"
                    title="See base metric description.">High
                    (H)</label>
            </div>

        </div>

    </fieldset>

    <fieldset class="font-weight-bold cvss-fieldset" id="CVSS_v4_environmentalMetricGroup">
        <legend id="CVSS_v4_environmentalMetricGroup_Legend"
            title="These metrics enable the consumer analyst to customize the resulting score depending on the importance of the affected IT asset to a user’s organization, measured in terms of complementary/alternative security controls in place, Confidentiality, Integrity, and Availability. The metrics are the modified equivalent of Base metrics and are assigned values based on the system placement within organizational infrastructure.">
            Environmental (Security Requirements)</legend>

        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v4_CR_Heading"
                    title="These metrics enable the consumer to customize the assessment depending on the importance of the affected IT asset to the analyst’s organization, measured in terms of Confidentiality, Integrity, and Availability. That is, if an IT asset supports a business function for which Availability is most important, the analyst can assign a greater value to Availability metrics relative to Confidentiality and Integrity. Each Security Requirement has three possible values: Low, Medium, or High, or the default value of Not Defined (X).
                        \nThe full effect on the environmental score is determined by the corresponding Modified Base Impact metrics. Following the concept of assuming 'reasonable worst case', in absence of explicit values, these metrics are set to the default value of Not Defined (X), which is equivalent to the metric value of High (H).
                        \nFor brevity, the same table is used for all three metrics. The lower the Security Requirement, the lower the score (recall that High is considered the default).">
                    Confidentiality Requirement (CR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V4_CR" value="X" id="CVSS_v4_CR_X" checked=""
                    type="radio" /><label for="CVSS_v4_CR_X" id="CVSS_v4_CR_X_Label"
                    title="This is the default value. Assigning this value indicates there is insufficient information to choose one of the other values. This has the same effect as assigning High as the worst case.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V4_CR" value="L" id="CVSS_v4_CR_L" type="radio" /><label
                    for="CVSS_v4_CR_L" id="CVSS_v4_CR_L_Label"
                    title="Loss of Confidentiality is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V4_CR" value="M" id="CVSS_v4_CR_M" type="radio" /><label
                    for="CVSS_v4_CR_M" id="CVSS_v4_CR_M_Label"
                    title="Loss of Confidentiality is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Medium (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V4_CR" value="H" id="CVSS_v4_CR_H" type="radio" /><label
                    for="CVSS_v4_CR_H" id="CVSS_v4_CR_H_Label"
                    title="Loss of Confidentiality is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_IR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the Integrity of the affected IT asset to a user’s organization, relative to other impacts. This metric modifies the environmental score by reweighting the Modified Integrity impact metric versus the other modified impacts.">
                    Integrity Requirement (IR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V4_IR" value="X" id="CVSS_v4_IR_X" checked=""
                    type="radio" /><label for="CVSS_v4_IR_X" id="CVSS_v4_IR_X_Label"
                    title="This is the default value. Assigning this value indicates there is insufficient information to choose one of the other values. This has the same effect as assigning High as the worst case.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V4_IR" value="L" id="CVSS_v4_IR_L" type="radio" /><label
                    for="CVSS_v4_IR_L" id="CVSS_v4_IR_L_Label"
                    title="Loss of Integrity is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V4_IR" value="M" id="CVSS_v4_IR_M" type="radio" /><label
                    for="CVSS_v4_IR_M" id="CVSS_v4_IR_M_Label"
                    title="Loss of Integrity is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Medium (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V4_IR" value="H" id="CVSS_v4_IR_H" type="radio" /><label
                    for="CVSS_v4_IR_H" id="CVSS_v4_IR_H_Label"
                    title="Loss of Integrity is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v4_AR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the Availability of the affected IT asset to a user’s organization, relative to other impacts. This metric modifies the environmental score by reweighting the Modified Availability impact metric versus the other modified impacts.">
                    Availability Requirement (AR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V4_AR" value="X" id="CVSS_v4_AR_X" checked=""
                    type="radio" /><label for="CVSS_v4_AR_X" id="CVSS_v4_AR_X_Label"
                    title="This is the default value. Assigning this value indicates there is insufficient information to choose one of the other values. This has the same effect as assigning High as the worst case.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V4_AR" value="L" id="CVSS_v4_AR_L" type="radio" /><label
                    for="CVSS_v4_AR_L" id="CVSS_v4_AR_L_Label"
                    title="Loss of Availability is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V4_AR" value="M" id="CVSS_v4_AR_M" type="radio" /><label
                    for="CVSS_v4_AR_M" id="CVSS_v4_AR_M_Label"
                    title="Loss of Availability is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Medium (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V4_AR" value="H" id="CVSS_v4_AR_H" type="radio" /><label
                    for="CVSS_v4_AR_H" id="CVSS_v4_AR_H_Label"
                    title="Loss of Availability is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High
                    (H)</label>
            </div>
        </div>
    </fieldset>

    <fieldset id="CVSS_v4_threatMetricGroup" class="cvss-fieldset">
            <legend id="CVSS_v4_threatMetricGroup_Legend"
                title="The Threat metrics measure the current state of exploit techniques or code availability for a vulnerability.">
                Threat Score</legend>

            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="CVSS_v4_E_Heading"
                        title="This metric measures the likelihood of the vulnerability being attacked, and is based on the current state of exploit techniques, exploit code availability, or active, 'in-the-wild' exploitation. Public availability of easy-to-use exploit code or exploitation instructions increases the number of potential attackers by including those who are unskilled. Initially, real-world exploitation may only be theoretical. Publication of proof-of-concept exploit code, functional exploit code, or sufficient technical details necessary to exploit the vulnerability may follow. Furthermore, the available exploit code or instructions may progress from a proof-of-concept demonstration to exploit code that is successful in exploiting the vulnerability consistently. In severe cases, it may be delivered as the payload of a network-based worm or virus or other automated attack tools.
                            \nIt is the responsibility of the CVSS consumer to populate the values of Exploit Maturity (E) based on information regarding the availability of exploitation code/processes and the state of exploitation techniques. This information will be referred to as 'threat intelligence' throughout this document.
                            \nOperational Recommendation: Threat intelligence sources that provide Exploit Maturity information for all vulnerabilities should be preferred over those with only partial coverage. Also, it is recommended to use multiple sources of threat intelligence as many are not comprehensive. This information should be updated as frequently as possible and its application to CVSS assessment should be automated.
                            \nThe more easily a vulnerability can be exploited, the higher the vulnerability score.">
                        Exploit Maturity (E)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V4_E" value="X" id="CVSS_v4_E_X" checked="" type="radio" /><label
                        for="CVSS_v4_E_X" id="CVSS_v4_E_X_Label"
                        title="Reliable threat intelligence is not available to determine Exploit Maturity characteristics. This is the default value and is equivalent to Attacked (A) for the purposes of the calculation of the score by assuming the worst case.">Not
                        Defined (X)</label>
                        
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V4_E" value="A" id="CVSS_v4_E_A" type="radio" /><label
                        for="CVSS_v4_E_A" id="CVSS_v4_E_A_Label"
                        title="	Based on available threat intelligence either of the following must apply: Attacks targeting this vulnerability (attempted or successful) have been reported Solutions to simplify attempts to exploit the vulnerability are publicly or privately available (such as exploit toolkits)">Attacked
                        (A)</label>
                        
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V4_E" value="P" id="CVSS_v4_E_P" type="radio" /><label
                        for="CVSS_v4_E_P" id="CVSS_v4_E_P_Label"
                        title="Based on available threat intelligence each of the following must apply: Proof-of-concept exploit code is publicly available No knowledge of reported attempts to exploit this vulnerability No knowledge of publicly available solutions used to simplify attempts to exploit the vulnerability (i.e., the 'Attacked' value does not apply)">POC
                        (P)</label>
                        
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V4_E" value="U" id="CVSS_v4_E_U" type="radio" /><label
                        for="CVSS_v4_E_U" id="CVSS_v4_E_U_Label"
                        title="Based on available threat intelligence each of the following must apply: No knowledge of publicly available proof-of-concept exploit code No knowledge of reported attempts to exploit this vulnerability No knowledge of publicly available solutions used to simplify attempts to exploit the vulnerability (i.e., neither the 'POC' nor 'Attacked' values apply)">Unreported
                        (U)</label>
                </div>
            </div>

    </fieldset>
</div>
`,
};
